import * as React from 'react'
import AsyncSelect from 'react-select/async'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { loadOrderNumberOptions, syncJobnumbers, getJobnumbersSyncStatus } from '../../../api/jobnumbers'
import { OrderNumberOption } from './types'
import OrderOption from './OrderOption'
import OrderMenuWrapper from './OrderMenuWrapper'
import OrderLoadingMessage from './OrderLoadingMessage'
import OrderNoOptionMessage from './OrderNoOptionMessage'
import InputWithCopyButton from './InputWithCopyButton'
import { JobNumberShape } from '../../../api/api_data_types'
import CommonContext from '../../CommonContext'

export interface OrderNumberPickerProps {
  orderNumber: JobNumberShape | null
  setOrderNumber: (jobnumber: JobNumberShape | null) => void
  hideCustomerTitle?: boolean
}

export default function OrderNumberPicker({
  orderNumber,
  setOrderNumber,
  hideCustomerTitle = false,
}: OrderNumberPickerProps) {
  const { dynamicsnavPrefix } = React.useContext(CommonContext) || {}
  const queryClient = useQueryClient()
  const [shouldCheckStatus, setShouldCheckStatus] = React.useState(false)

  const { data: syncStatus } = useQuery('jobnumbers-sync-status', getJobnumbersSyncStatus, {
    enabled: shouldCheckStatus,
    refetchInterval: (data) => {
      return data?.status === 'in_progress' ? 2000 : false
    },
    onSuccess: (data) => {
      if (data.status === 'idle') {
        queryClient.invalidateQueries('jobnumbers')
        setShouldCheckStatus(false)
      }
    },
  })

  const syncMutation = useMutation(syncJobnumbers, {
    onMutate: () => {
      // Optimistically set the status to in_progress
      queryClient.setQueryData('jobnumbers-sync-status', { status: 'in_progress' })
    },
    onSuccess: () => {
      // Enable status checking only after sync has been triggered
      setShouldCheckStatus(true)
      // Small delay to ensure the job is enqueued before we start polling
      setTimeout(() => {
        queryClient.invalidateQueries('jobnumbers-sync-status')
      }, 100)
    },
  })

  const handleSync = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (syncStatus?.status !== 'in_progress') {
      syncMutation.mutate()
    }
  }

  const handleChange = function (e: OrderNumberOption | null) {
    const jn = e ? e.jobnumber : null
    setOrderNumber(jn)
  }

  const customerTitle = (orderNumber && orderNumber.customer_title) || ''
  const selectedValue = orderNumber
    ? {
        value: orderNumber.id,
        label: orderNumber.order_number_title || '',
        jobnumber: orderNumber,
      }
    : null
  const groupClassName = 'col-sm-12 form-group'

  const isSyncing = syncStatus?.status === 'in_progress' || syncMutation.isLoading

  return (
    <div className="row">
      <div className={groupClassName}>
        <div className="d-flex align-items-center justify-content-between">
          <label>Ordrenummer, Sags-ID eller beskrivelse</label>
          <button
            type="button"
            onClick={handleSync}
            disabled={isSyncing}
            style={{
              background: 'none',
              border: 'none',
              cursor: isSyncing ? 'default' : 'pointer',
              padding: '0 0.7rem',
              color: '#4d7d75',
            }}
            title={isSyncing ? 'Synkroniserer...' : 'Synkroniser jobnumre'}
          >
            {isSyncing ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-sync" />}
          </button>
        </div>
        <AsyncSelect<OrderNumberOption, false>
          components={{
            Option: OrderOption,
            MenuList: OrderMenuWrapper,
            LoadingMessage: OrderLoadingMessage,
            NoOptionsMessage: OrderNoOptionMessage,
          }}
          id="record-jobnumber-select"
          placeholder="Vælg eller skriv..."
          loadOptions={loadOrderNumberOptions}
          defaultOptions
          cacheOptions
          isClearable
          value={selectedValue}
          onChange={(e) => handleChange(e)}
          styles={{ menu: (base) => ({ ...base, zIndex: 1100 }) }} // more than sticky headers have
          required
        />
        <input
          tabIndex={-1}
          autoComplete="off"
          style={{ opacity: 0, height: 0, position: 'absolute' }}
          value={selectedValue ? selectedValue.value : ''}
          required
          readOnly
        />
      </div>
      {!hideCustomerTitle && orderNumber && (
        <div className="col-sm-12 form-group form-inline">
          <label>ID: </label>
          <InputWithCopyButton value={orderNumber.job_id} />
          <span>Ordrenummer: </span>
          <InputWithCopyButton value={orderNumber.order_number || ''} />
          {dynamicsnavPrefix && orderNumber.job_id && (
            <a href={dynamicsnavPrefix + orderNumber.job_id} className="btn btn-sm btn-outline-secondary ml-1">
              Åbn i NAV
            </a>
          )}
        </div>
      )}
      {!hideCustomerTitle && (
        <div className={groupClassName}>
          <label>Kunde</label>
          <input className="form-control record-jobnumber-company-name" disabled value={customerTitle} />
        </div>
      )}
    </div>
  )
}
